import * as React from 'react';
import { useCallback, useContext, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import moment from 'moment';
import _ from 'lodash';

import '@/lib/string';
import { useCachedValue } from '@/lib/react-hooks';
import { abortMessage, editMessage, updateMessage } from '@/redux/app/actions/event';
import { NavigationPaneRouteParamsContext } from '@/lib/navigation';
import type { EventElementProps } from './event-element-props.interface';

export interface PendingMessageProps extends EventElementProps {
  ticket: string;
  eventId: string;
}

export const PendingMessage = ({ data, ticket, eventId }: PendingMessageProps) => {
  let status: string;
  let statusDescription: string;
  if (data.aborted) {
    status = 'failed';
    statusDescription = 'Cancelled';
  } else if (data.sent?.element_id) {
    status = 'success';
    statusDescription = 'Scheduled and Sent';
  } else {
    status = 'pending';
    statusDescription = 'Follow up Scheduled for';
  }

  let cancelInfo: string;
  if (data.cancel_on === 'OPT_OUT') {
    cancelInfo = 'Pending message will be Cancelled upon Opt Out';
  } else if (data.cancel_on === 'RESPONSE') {
    cancelInfo = 'Pending message will be Cancelled upon Response';
  }

  // If aborted prop is missing, we haven't received created event data from the server yet.
  const sendable = typeof data.aborted === 'boolean' && !data.aborted && !data.sent?.element_id;

  const { subdomain } = useContext(NavigationPaneRouteParamsContext);
  const dispatch = useDispatch();
  const handleCancel = useCallback(() => {
    if (window.confirm('Are you sure you want to cancel this scheduled message?')) {
      dispatch(abortMessage(subdomain, ticket, data.id, eventId));
    }
  }, [ticket, eventId, subdomain, data.id, dispatch]);

  const inputRef = useRef<HTMLDivElement>();
  const handleInput = useCallback(_.throttle(() => {
    dispatch(editMessage(ticket, eventId, inputRef.current.innerHTML));
  }, 1000), [ticket, eventId, inputRef, dispatch]);
  const handlePressEdit = useCallback(() => {
    inputRef.current.focus();
  }, [inputRef]);
  const handleUpdate = useCallback(() => {
    const newValue = inputRef.current.innerHTML;
    dispatch(updateMessage({ subdomain, ticket, messageId: data.id, eventId, newValue }));
  }, [ticket, eventId, subdomain, data.id, dispatch]);

  const [editing, setEditing] = useState(false);
  const handleFocus = useCallback(() => {
    setEditing(true);
  }, [setEditing]);
  const handleBlur = useCallback(() => {
    setEditing(false);
  }, [setEditing]);
  const messageHtml = useCachedValue(
    () => data.message_html.toReactHtml({ linkify: true }),
    !editing,
  );

  return (
    <div
      id={`PendingMessage-${data.id}`}
      className={classNames('pending_message', 'message-box', { 'hide-important': !!data.sent?.element_id })}
      data-status={status}
      data-direction="out"
    >
      {sendable && (
        <button type="button" className="destroy" onClick={handleCancel} onKeyPress={handleCancel}>Cancel</button>
      )}

      <div className={classNames('outbound-message', 'pending', { editing })}>
        {data.media_url && (
          <a href={data.media_url} target="_blank" rel="noreferrer" className="attachment-link">Added Attachment</a>
        )}
        <div
          ref={inputRef}
          tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
          className="message-text"
          contentEditable={sendable}
          onInput={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={messageHtml}
        />
        {sendable && (
          <div className="actions">
            {(editing || data.dirty
              ? <button type="button" className="save-pending-message" onClick={handleUpdate} onKeyPress={handleUpdate}>save</button>
              : <button type="button" className="message-updater" onClick={handlePressEdit} onKeyPress={handlePressEdit}>edit message</button>
            )}
          </div>
        )}
      </div>

      <div className="chat-timestamp stamp-out">
        <span className="sender">{data.sender_tag}</span>
        {` - ${statusDescription} ${moment(
          data[data.aborted || data.sent?.element_id ? 'updated_at' : 'scheduled_for'] * 1000,
        ).format('L [AT] LT')}`}
      </div>

      {data.aborted && (
        <div className="cancel-on-data">
          {data.aborter_id !== 0 && `${data.aborted_by} - `}
          {data.abort_reason}
        </div>
      )}
      {!data.aborted && data.cancel_on && (
        <div className="cancel-on-data">
          {cancelInfo}
        </div>
      )}
    </div>
  );
};
